import { CashApyDisclosure, CashBackDisclosure } from '@fruitful/compliance';
import { AnalyticsUserEvent } from '@fruitful/types';
import { Redirect, router } from 'expo-router';
import React, { useCallback, useRef, useState } from 'react';
import {
    BodyMediumEmphasized,
    BodyMediumRegular,
    BodySmallRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    FullScreenSpinner,
    LabelRegular,
    ScreenContainer,
    Sheet,
    SuperscriptText,
    useToastController,
    YStack,
} from 'smoothie';

import { AccountsSection } from '../../../banking/components/add-card/AccountsSection';
import {
    AutoPayDialog,
    MoneyMapAutoPaySection,
} from '../../../banking/components/add-card/AutoPaySection';
import { CardDetailsSection } from '../../../banking/components/add-card/CardDetailsSection';
import { BatchOpenCardBankingTerms } from '../../../banking/components/BankingTerms';
import {
    BANKING_GENERAL_DISCLOSURE_TEXT,
    FRUITFUL_BATCH_OPEN_CARD_POLICY_DISCLOSURES,
    FRUITFUL_BATCH_OPEN_CASH_POLICY_DISCLOSURES,
    getBankingDisclosures,
} from '../../../banking/components/Disclosures';
import { KYCVerificationStatus } from '../../../banking/types/BankAccount';
import { useAuthedUser } from '../../../common/providers/AuthedUserProvider';
import { useViewEvents } from '../../../events/hooks/useViewEvents';
import { captureException, track } from '../../../logger/Logger';
import { SUPPORT_EMAIL } from '../../../settings/types/constants';
import { useCreateMoneyMapAccounts } from '../../hooks/useCreateMoneyMapAccounts';
import { useMoneyMapWorkflow } from '../../hooks/useMoneyMapWorkflow';
import { GoWorkflowStepType } from '../../types/GoWorkflow';
import { GoWorkflowHeader } from '../GoWorkflowHeader';
import { AccountsStepSuccess } from './accounts/AccountsStepSuccess';

export const CardAccounts = () => {
    const { trackViewOnce } = useViewEvents();
    const toast = useToastController();
    const user = useAuthedUser();

    const [acceptTerms, setAcceptTerms] = useState<boolean>(false);
    const [isAutoPayEnabled, setIsAutoPayEnabled] = useState<boolean>(false);
    const [showAutoPayDialog, setShowAutoPayDialog] = useState<boolean>(false);
    const [isCreatingAccounts, setIsCreatingAccounts] =
        useState<boolean>(false);
    const [showSuccessScreen, setShowSuccessScreen] = useState<boolean>(false);

    const {
        isLoadingMoneyMap,
        moneyMap,
        hasMoneyMapError,
        isLoadingCompleteness,
        refetchMoneyMapCompleteness,
        moneyMapCompleteness,
        workflowSteps,
        trackWorkflowCompletedEvent,
    } = useMoneyMapWorkflow();
    const { cardAccountsStep, cashAccountsStep } =
        // use ref because we don't want this and things derived from it to change after step is submitted
        useRef(moneyMapCompleteness).current;
    const {
        isIncluded,
        isEnabled,
        memberSoloAccountsToOpen: soloAccountsToOpen,
        memberJointAccountsToAccept: jointAccountsToAccept,
        jointAccountsToOpen,
        partnerJointAccountsToAccept,
        partnerSoloAccountsToOpen,
    } = cardAccountsStep;
    const { mutate: createAccounts } = useCreateMoneyMapAccounts();

    const accountsToOpen = [...soloAccountsToOpen, ...jointAccountsToOpen];
    const memberHasAccountsToOpen = accountsToOpen.length > 0;
    const memberHasAccountsToAccept = jointAccountsToAccept.length > 0;
    const isAccountInvitationsOnly =
        memberHasAccountsToAccept && !memberHasAccountsToOpen;

    const submitCardAccountsStep = useCallback(
        (isAutoPayOn: boolean) => {
            track(
                AnalyticsUserEvent.MONEY_MAP_WORKFLOW_STEP_SUBMIT_CARD_ACCOUNTS,
            );
            setIsCreatingAccounts(true);

            // TODO move most of this to be within the useCreateMoneyMapAccounts hook to be shared across
            // this step and the card step.
            createAccounts(
                {
                    urlParams: {
                        id: moneyMap?.id ?? '',
                    },
                    body: {
                        disclosures: {
                            disclosures: getBankingDisclosures({
                                withKyc: false,
                                enableAutoPay: isAutoPayOn,
                                policyTypes: [
                                    ...FRUITFUL_BATCH_OPEN_CARD_POLICY_DISCLOSURES,
                                    ...FRUITFUL_BATCH_OPEN_CASH_POLICY_DISCLOSURES, // These are from the previous step
                                ],
                            }),
                        },
                        submittedCardAccounts: {
                            memberSoloAccountsToOpen: soloAccountsToOpen.map(
                                (a) => a.id,
                            ),
                            jointAccountsToOpen: jointAccountsToOpen.map(
                                (a) => a.id,
                            ),
                            memberJointAccountsToAccept:
                                jointAccountsToAccept.map((a) => a.id),
                            partnerJointAccountsToAccept:
                                partnerJointAccountsToAccept.map((a) => a.id),
                            partnerSoloAccountsToOpen:
                                partnerSoloAccountsToOpen.map((a) => a.id),
                        },
                        submittedCashAccounts: {
                            memberSoloAccountsToOpen:
                                cashAccountsStep.memberSoloAccountsToOpen.map(
                                    (a) => a.id,
                                ),
                            jointAccountsToOpen:
                                cashAccountsStep.jointAccountsToOpen.map(
                                    (a) => a.id,
                                ),
                            memberJointAccountsToAccept:
                                cashAccountsStep.memberJointAccountsToAccept.map(
                                    (a) => a.id,
                                ),
                            partnerJointAccountsToAccept:
                                cashAccountsStep.partnerJointAccountsToAccept.map(
                                    (a) => a.id,
                                ),
                            partnerSoloAccountsToOpen:
                                cashAccountsStep.partnerSoloAccountsToOpen.map(
                                    (a) => a.id,
                                ),
                        },
                        enableAutoPay: isAutoPayOn,
                    },
                },
                {
                    onSettled: async (result) => {
                        if (result?.success === false) {
                            toast.show(
                                `There was an error while creating your Money Map accounts. Please reach out to support at ${SUPPORT_EMAIL}`,
                            );
                            router.replace('/roadmap/go/start');
                        } else {
                            track(
                                AnalyticsUserEvent.MONEY_MAP_WORKFLOW_ACCOUNTS_COMPLETE,
                            );
                            setShowSuccessScreen(true);
                            await refetchMoneyMapCompleteness();
                            trackWorkflowCompletedEvent(
                                GoWorkflowStepType.CARD_ACCOUNTS,
                            );
                            setIsCreatingAccounts(false);
                        }
                    },
                },
            );
        },
        [moneyMap, createAccounts, refetchMoneyMapCompleteness, workflowSteps],
    );

    const [showSheet, setShowSheet] = useState<boolean>(false);

    const onLearnMorePress = useCallback(() => {
        track(AnalyticsUserEvent.MONEY_MAP_WORKFLOW_CARD_ACCOUNTS_SHEET_OPENED);
        setShowSheet(true);
    }, [track]);

    const onSheetClose = useCallback(() => {
        track(AnalyticsUserEvent.MONEY_MAP_WORKFLOW_CARD_ACCOUNTS_SHEET_CLOSED);
        setShowSheet(false);
    }, [track]);

    if (isLoadingMoneyMap || isCreatingAccounts || isLoadingCompleteness) {
        return <FullScreenSpinner />;
    }

    if (showSuccessScreen) {
        return (
            <AccountsStepSuccess
                currentStepType={GoWorkflowStepType.CARD_ACCOUNTS}
                isAccountInvitationsOnly={isAccountInvitationsOnly}
            />
        );
    }

    if (moneyMap == null || hasMoneyMapError) {
        captureException(
            new Error('Tried to load CardAccounts step with no Money Map'),
        );
        toast.show(
            `There was an error while loading your Money Map.  Please reach out to support at ${SUPPORT_EMAIL}`,
        );
        return <Redirect href="/home/roadmap" />;
    }

    // Should not be possible to trigger this, but just in case.
    if (
        !isIncluded ||
        !isEnabled ||
        !(user.syncteraKycStatus === KYCVerificationStatus.ACCEPTED)
    ) {
        captureException(
            new Error('Cannot load CardAccounts step: invalid state'),
            {
                isIncluded,
                isEnabled,
                kycStatus: user.syncteraKycStatus,
                memberHasAccountsToOpen,
                memberHasAccountsToAccept,
            },
        );
        toast.show('There was an issue with the Card Accounts step.');
        return <Redirect href="/roadmap/go/start" />;
    }

    trackViewOnce('Roadmap/Go/CardAccounts');

    return (
        <>
            <GoWorkflowHeader
                stepType={GoWorkflowStepType.CARD_ACCOUNTS}
                title="Open Fruitful Card Accounts"
            />
            <ScreenContainer useSafeAreaInsetTop={false}>
                <YStack marginTop={12} justifyContent="space-between">
                    <YStack gap={16}>
                        <AccountsSection
                            currentStepType={GoWorkflowStepType.CARD_ACCOUNTS}
                            soloAccountsToOpen={soloAccountsToOpen}
                            jointAccountsToOpen={jointAccountsToOpen}
                            jointAccountsToAccept={jointAccountsToAccept}
                            learnMoreText={
                                <SuperscriptText
                                    superscript={2}
                                    textBefore="Fruitful cards earn up to 2% cash back"
                                    textAfter="on all purchases."
                                    textComponent={BodySmallRegular}
                                    textProps={{ flex: 1 }}
                                >
                                    {' '}
                                    <BodySmallRegular textDecorationLine="underline">
                                        Learn more
                                    </BodySmallRegular>
                                </SuperscriptText>
                            }
                            onLearnMorePress={onLearnMorePress}
                        />
                    </YStack>
                    <YStack width="100%" gap={16}>
                        <BatchOpenCardBankingTerms
                            termsAccepted={acceptTerms}
                            onPress={() => {
                                setAcceptTerms((oldVal) => {
                                    return !oldVal;
                                });
                            }}
                            backgroundColor="$gray200"
                            padding={16}
                            borderRadius={16}
                        />

                        <CardDetailsSection
                            accountsToOpen={accountsToOpen}
                            accountsToAccept={jointAccountsToAccept}
                            user={user}
                        />
                        <MoneyMapAutoPaySection
                            numberOfAccountsToOpen={accountsToOpen.length}
                            memberHasAccountsToAccept={
                                memberHasAccountsToAccept
                            }
                            isChecked={isAutoPayEnabled}
                            onChange={() => {
                                setIsAutoPayEnabled((oldValue) => {
                                    return !oldValue;
                                });
                            }}
                            enabled={true}
                        />
                        <Button
                            disabled={!acceptTerms}
                            text="Accept and submit"
                            size={ButtonSize.LARGE}
                            variant={ButtonVariant.PRIMARY}
                            onPress={() => {
                                if (!memberHasAccountsToOpen) {
                                    // if not opening accounts, autopay is irrelevant
                                    submitCardAccountsStep(false);
                                } else if (isAutoPayEnabled) {
                                    submitCardAccountsStep(true);
                                } else {
                                    setShowAutoPayDialog(true);
                                }
                            }}
                        />
                        <YStack marginBottom={40} width="100%" gap={12}>
                            <LabelRegular color="$gray900">
                                Fruitful is a financial technology company, not
                                a bank. Deposit accounts provided by Emigrant
                                Bank, Member FDIC. The Fruitful Card is issued
                                by Emigrant Bank pursuant to license by
                                Mastercard International Incorporated.
                            </LabelRegular>
                            <CashBackDisclosure superscript={2} />
                        </YStack>
                    </YStack>
                </YStack>
                <AutoPayDialog
                    isOpen={showAutoPayDialog}
                    onClose={() => {
                        track(
                            AnalyticsUserEvent.MONEY_MAP_WORKFLOW_AUTOPAY_PROMPT,
                            {
                                action: 'dismiss',
                            },
                        );
                        setShowAutoPayDialog(false);
                    }}
                    onNoAutoPay={() => {
                        track(
                            AnalyticsUserEvent.MONEY_MAP_WORKFLOW_AUTOPAY_PROMPT,
                            {
                                action: 'continue-without-autopay',
                            },
                        );
                        setShowAutoPayDialog(false);
                        setIsAutoPayEnabled(false);
                        submitCardAccountsStep(false);
                    }}
                    onYesAutoPay={() => {
                        track(
                            AnalyticsUserEvent.MONEY_MAP_WORKFLOW_AUTOPAY_PROMPT,
                            {
                                action: 'enable-autopay',
                            },
                        );
                        setShowAutoPayDialog(false);
                        setIsAutoPayEnabled(true);
                    }}
                />
            </ScreenContainer>
            <Sheet
                isOpen={showSheet}
                onClose={onSheetClose}
                showCloseButton={true}
                dismissOnOverlayPress={false}
                disableDrag={true}
                title="About Card Accounts"
            >
                <YStack width="100%" gap={16}>
                    <YStack width="100%" gap={4}>
                        <BodyMediumEmphasized>
                            Secured Charge Card
                        </BodyMediumEmphasized>
                        <BodyMediumRegular>
                            Fruitful Card Accounts are secured charge cards –
                            not debit cards. Card limits are tied to the balance
                            of Fruitful Cash Accounts, so you can&apos;t
                            overspend.
                        </BodyMediumRegular>
                    </YStack>
                    <YStack width="100%" gap={4}>
                        <BodyMediumEmphasized>
                            Reserved Funds
                        </BodyMediumEmphasized>
                        <SuperscriptText
                            superscript={1}
                            textBefore="Funds are set aside as you spend, so your Cash Account balance keeps earning 4.00% APY"
                            textComponent={BodyMediumRegular}
                        />
                    </YStack>
                    <YStack width="100%" gap={4}>
                        <SuperscriptText
                            superscript={2}
                            textBefore="2% Cash Back"
                            textComponent={BodyMediumEmphasized}
                        />
                        <BodyMediumRegular>
                            Up to 2% cash back on all purchases.
                        </BodyMediumRegular>
                    </YStack>
                    <Button
                        marginTop={8}
                        text="Got it"
                        size={ButtonSize.LARGE}
                        variant={ButtonVariant.PRIMARY}
                        onPress={onSheetClose}
                    />
                    <LabelRegular color="$gray900">
                        {BANKING_GENERAL_DISCLOSURE_TEXT}
                    </LabelRegular>
                    <CashApyDisclosure superscript={1} />
                    <CashBackDisclosure superscript={2} />
                </YStack>
            </Sheet>
        </>
    );
};
